import React, { useState, useRef } from 'react';

import VisuallyHidden from 'components/accessibility/VisuallyHidden/VisuallyHidden';
import PropTypes from 'prop-types';
import { keyGenerator, ordinal } from 'utils/index';

import styles from './OTPInput.module.scss';

// eslint-disable-next-line arrow-body-style
const OTPInput = ({ onSubmit, description, length, pauseBeforeSubmit }) => {
  const initialValues = new Array(length);
  const [values, setValues] = useState(initialValues.fill(''));
  const refStore = [...initialValues].map(() => useRef(null));

  const safeKeycodes = [
    48,
    49,
    50,
    51,
    52,
    53,
    54,
    55,
    56,
    57,
    96,
    97,
    98,
    99,
    100,
    101,
    102,
    103,
    104,
    105
  ];

  const updateValuesFromTarget = (index, target) => {
    const { value } = target;
    setValues((newValues) => {
      newValues.splice(index, 1, value);
      return [...newValues];
    });
  };

  const focusPrevious = (index, removeValue) => {
    const newIndex = index - 1;
    const input = refStore[newIndex].current;
    input.focus();
    if (removeValue) {
      input.value = '';
      updateValuesFromTarget(newIndex, input);
    }
  };
  const focusNext = (index) => refStore[index + 1].current.focus();

  const unFocus = () => refStore.forEach((ref) => ref.current.blur());

  const isFirst = (index) => !index;
  const isLast = (index) => index === length - 1;

  const handleKeyUp = () => () => {
    if (!values.includes(undefined) && !values.includes('')) {
      unFocus();
      setTimeout(() => {
        onSubmit(values.join(''));
      }, pauseBeforeSubmit);
    }
  };

  const handleKeyDown = (index) => (event) => {
    const { target, keyCode } = event;
    if (keyCode === 37 && !isFirst(index)) {
      focusPrevious(index);
    } else if (keyCode === 8 && !isFirst(index)) {
      focusPrevious(index, true);
    } else if (keyCode === 39 && !isLast(index)) {
      focusNext(index);
    } else {
      if (safeKeycodes.includes(keyCode)) {
        // eslint-disable-next-line max-len
        target.value = String.fromCharCode(
          keyCode >= 96 && keyCode <= 105 ? keyCode - 48 : keyCode
        );
      }
      updateValuesFromTarget(index, target);
      if (safeKeycodes.includes(keyCode) && !isLast(index)) {
        focusNext(index);
      }
    }
  };

  const handlePaste = ({ clipboardData }) => {
    const pastedText = clipboardData.getData('text/plain');
    const textArray = pastedText.split('').slice(0, length);
    setValues(textArray);
  };

  const renderInputs = () =>
    [...values].map((value, index) => (
      <input
        id={`otp-${index + 1}`}
        ref={refStore[index]}
        type="text"
        pattern="[0-9]*"
        maxLength="1"
        value={value}
        onKeyUp={handleKeyUp(index)}
        onKeyDown={handleKeyDown(index)}
        className={styles.input}
        key={keyGenerator('otp-input', index)}
        aria-describedby="otp-description"
        aria-labelledby={`otp-label-${index + 1}`}
        required
      />
    ));

  const generateLabels = () =>
    [...values].map((_, index) => (
      <label
        key={keyGenerator('otp-label', index)}
        htmlFor={`otp-${index + 1}`}
        id={`otp-label-${index + 1}`}
      >{`${ordinal(index + 1)} digit of passcode`}</label>
    ));

  return (
    <form className={styles.container} onPaste={handlePaste}>
      <VisuallyHidden>
        <span id="otp-description">{description}</span>
        {generateLabels()}
      </VisuallyHidden>
      {renderInputs()}
    </form>
  );
};

OTPInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  length: PropTypes.number,
  pauseBeforeSubmit: PropTypes.number
};

OTPInput.defaultProps = {
  length: 4,
  pauseBeforeSubmit: 200
};

export default OTPInput;
